import React, { useEffect, useState } from 'react';
import { useApp } from '../contexts/AppContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, X, Clock, Minus, Search, Download } from 'lucide-react';
import ExportBar from '../components/ExportBar';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AttendanceMarking() {
  const { t, lang, currentSchool, currentTeacher } = useApp();
  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [selectedGrade, setSelectedGrade] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [search, setSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (currentSchool) {
      const params = { school_id: currentSchool.school_id };
      if (selectedGrade) params.grade = selectedGrade;
      if (selectedSection) params.section = selectedSection;
      axios.get(`${API}/schools/${currentSchool.school_id}/students`, { params })
        .then(r => {
          setStudents(r.data);
          const att = {};
          r.data.forEach(s => { att[s.student_id] = 'present'; });
          setAttendance(att);
        }).catch(() => {});
    }
  }, [currentSchool, selectedGrade, selectedSection]);

  const filteredStudents = students.filter(s =>
    !search || s.name_bangla.includes(search) || (s.name_english && s.name_english.toLowerCase().includes(search.toLowerCase()))
  );

  const markAll = (status) => {
    const newAtt = {};
    students.forEach(s => { newAtt[s.student_id] = status; });
    setAttendance(newAtt);
  };

  const handleSubmit = async () => {
    if (!currentTeacher || !currentSchool) return;
    setSubmitting(true);
    try {
      const records = Object.entries(attendance).map(([student_id, status]) => ({
        student_id,
        teacher_id: currentTeacher.teacher_id,
        school_id: currentSchool.school_id,
        status,
        gps_lat: currentSchool.gps_lat + (Math.random() - 0.5) * 0.002,
        gps_lng: currentSchool.gps_lng + (Math.random() - 0.5) * 0.002,
      }));
      const res = await axios.post(`${API}/attendance/mark`, { records });
      toast.success(t('att_saved'));
      setSubmitted(true);
    } catch (e) {
      toast.error('Failed to submit');
    }
    setSubmitting(false);
  };

  const statusConfig = {
    present: { icon: Check, color: 'bg-emerald-500 text-white', label: t('att_present') },
    absent: { icon: X, color: 'bg-red-600 text-white', label: t('att_absent') },
    late: { icon: Clock, color: 'bg-amber-500 text-white', label: t('att_late') },
    excused: { icon: Minus, color: 'bg-slate-400 text-white', label: t('att_excused') },
  };

  const grades = [...new Set(students.map(s => s.grade))].sort();
  const sections = [...new Set(students.map(s => s.section))].sort();

  const presentCount = Object.values(attendance).filter(s => s === 'present').length;
  const absentCount = Object.values(attendance).filter(s => s === 'absent').length;
  const lateCount = Object.values(attendance).filter(s => s === 'late').length;

  return (
    <div className="space-y-4 animate-fade-in" data-testid="attendance-marking">
      <div className="card-flat p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold">{t('att_mark')}</h2>
          <span className="text-xs text-slate-400">{new Date().toLocaleDateString(lang === 'bn' ? 'bn-BD' : 'en-US')}</span>
        </div>

        <div className="flex gap-2 mb-3">
          <select
            data-testid="grade-select"
            value={selectedGrade}
            onChange={e => setSelectedGrade(e.target.value)}
            className="text-sm border border-slate-200 rounded px-2 py-1.5 flex-1"
          >
            <option value="">{t('att_select_class')}</option>
            {Array.from({length: 12}, (_, i) => i + 1).map(g => <option key={g} value={g}>{t('lesson_grade')} {g}</option>)}
          </select>
          <select
            data-testid="section-select"
            value={selectedSection}
            onChange={e => setSelectedSection(e.target.value)}
            className="text-sm border border-slate-200 rounded px-2 py-1.5 flex-1"
          >
            <option value="">{t('att_select_section')}</option>
            {sections.map(s => {
              const sectionLabel = lang === 'bn' ? s : (s === 'ক' ? 'A' : s === 'খ' ? 'B' : s === 'গ' ? 'C' : s);
              return <option key={s} value={s}>{lang === 'bn' ? t('att_select_section').split(' ')[0] : 'Section'} {sectionLabel}</option>;
            })}
          </select>
        </div>

        <div className="relative mb-3">
          <Search size={16} className="absolute left-2.5 top-2.5 text-slate-400" />
          <input
            data-testid="student-search"
            type="text"
            placeholder={t('search')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded font-bangla"
          />
        </div>

        <div className="flex gap-2 mb-3">
          <button
            data-testid="mark-all-present"
            onClick={() => markAll('present')}
            className="text-xs px-3 py-1.5 bg-emerald-500 text-white rounded hover:bg-emerald-600 transition-colors"
          >
            {t('att_mark_all')}
          </button>
          <div className="flex items-center gap-3 ml-auto text-xs text-slate-500">
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-emerald-500 rounded-full" />{presentCount}</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-red-600 rounded-full" />{absentCount}</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-amber-500 rounded-full" />{lateCount}</span>
          </div>
        </div>
      </div>

      <div className="space-y-1.5">
        {filteredStudents.map(student => {
          const currentStatus = attendance[student.student_id] || 'present';
          return (
            <div
              key={student.student_id}
              className="card-flat p-3 flex items-center justify-between"
              data-testid={`student-row-${student.roll_number}`}
            >
              <div className="flex items-center gap-3">
                <span className="w-7 h-7 rounded-sm bg-slate-100 flex items-center justify-center text-xs font-medium text-slate-600">
                  {student.roll_number}
                </span>
                <div>
                  <p className="text-sm font-medium text-slate-800">{lang === 'bn' ? student.name_bangla : (student.name_english || student.name_bangla)}</p>
                  <p className="text-[10px] text-slate-400">
                    {t('lesson_grade')} {student.grade} | {lang === 'bn' ? student.section : (student.section_english || student.section)}
                    {student.welfare_flag && <span className="text-red-500 ml-1"> (!) {student.consecutive_absences} {t('att_absent')}</span>}
                  </p>
                </div>
              </div>
              <div className="flex gap-1">
                {Object.entries(statusConfig).map(([status, cfg]) => {
                  const Icon = cfg.icon;
                  const isActive = currentStatus === status;
                  return (
                    <button
                      key={status}
                      data-testid={`mark-${status}-${student.roll_number}`}
                      onClick={() => setAttendance(prev => ({ ...prev, [student.student_id]: status }))}
                      className={`w-8 h-8 rounded-sm flex items-center justify-center transition-all ${
                        isActive ? cfg.color : 'bg-slate-100 text-slate-400'
                      }`}
                    >
                      <Icon size={14} />
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {students.length > 0 && !submitted && (
        <button
          data-testid="submit-attendance"
          onClick={handleSubmit}
          disabled={submitting}
          className="w-full py-3 bg-[#1B2A4A] text-white rounded-sm font-semibold text-sm hover:bg-[#253a5e] transition-colors disabled:opacity-50"
        >
          {submitting ? t('loading') : t('att_submit')}
        </button>
      )}
      {submitted && (
        <div className="text-center py-3 bg-emerald-50 text-emerald-700 rounded-sm text-sm border border-emerald-200">
          {t('att_saved')}
        </div>
      )}
    </div>
  );
}

function AttendanceReport() {
  const { t, currentSchool, lang } = useApp();
  const [report, setReport] = useState(null);
  const [schools, setSchools] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState('');
  const [adminTab, setAdminTab] = useState('report');

  useEffect(() => { axios.get(`${API}/schools`).then(r => setSchools(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const sid = selectedSchool || currentSchool?.school_id;
    if (sid) axios.get(`${API}/attendance/report`, { params: { school_id: sid } }).then(r => setReport(r.data)).catch(() => {});
  }, [selectedSchool, currentSchool]);

  const ABSENCE_DATA = [
    { name: 'Rakib Hasan', nameBn: 'রাকিব হাসান', grade: 3, days: 7, reason: 'Family emergency', status: 'pending', parentAttempts: 1 },
    { name: 'Nusrat Jahan', nameBn: 'নুসরাত জাহান', grade: 5, days: 5, reason: 'Illness — dengue fever', status: 'approved', parentAttempts: 2 },
    { name: 'Tanvir Hossain', nameBn: 'তানভীর হোসেন', grade: 2, days: 12, reason: 'No response from parents', status: 'struck_off', parentAttempts: 3 },
    { name: 'Fariha Tasnim', nameBn: 'ফারিহা তাসনিম', grade: 4, days: 4, reason: 'Waiting for parent response', status: 'pending', parentAttempts: 0 },
    { name: 'Arif Islam', nameBn: 'আরিফ ইসলাম', grade: 1, days: 6, reason: 'Migration — family moved temporarily', status: 'long_absence', parentAttempts: 3 },
  ];
  const LEADERBOARD = [
    { name: 'Sanjida Akter', nameBn: 'সানজিদা আক্তার', grade: 5, present: 98, late: 1, absent: 1 },
    { name: 'Maria Islam', nameBn: 'মারিয়া ইসলাম', grade: 3, present: 97, late: 2, absent: 1 },
    { name: 'Sumaiya Khatun', nameBn: 'সুমাইয়া খাতুন', grade: 4, present: 96, late: 1, absent: 3 },
    { name: 'Rakib Hasan', nameBn: 'রাকিব হাসান', grade: 2, present: 95, late: 3, absent: 2 },
    { name: 'Nafis Ahmed', nameBn: 'নাফিস আহমেদ', grade: 5, present: 94, late: 2, absent: 4 },
    { name: 'Priya Rani', nameBn: 'প্রিয়া রানী', grade: 1, present: 93, late: 4, absent: 3 },
    { name: 'Juwel Rana', nameBn: 'জুয়েল রানা', grade: 3, present: 90, late: 5, absent: 5 },
    { name: 'Tushar Ahmed', nameBn: 'তুষার আহমেদ', grade: 4, present: 88, late: 2, absent: 10 },
  ];
  const LATECOMER_DATA = [
    { name: 'Milon Mia', nameBn: 'মিলন মিয়া', grade: 2, lateDays: 15, avgMinutesLate: 22 },
    { name: 'Rubel Hossain', nameBn: 'রুবেল হোসেন', grade: 5, lateDays: 12, avgMinutesLate: 18 },
    { name: 'Akash Mia', nameBn: 'আকাশ মিয়া', grade: 3, lateDays: 10, avgMinutesLate: 30 },
  ];

  return (
    <div className="space-y-5" data-testid="attendance-admin">
      {/* Tabs */}
      <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
        {[
          { id: 'report', en: 'Attendance Report', bn: 'হাজিরা প্রতিবেদন' },
          { id: 'absence', en: 'Absence Approval', bn: 'অনুপস্থিতি অনুমোদন' },
          { id: 'leaderboard', en: 'Attendance Leaderboard', bn: 'হাজিরা লিডারবোর্ড' },
        ].map(tab => (
          <button key={tab.id} onClick={() => setAdminTab(tab.id)} className="btn btn-sm"
            style={{ background: adminTab === tab.id ? 'var(--color-accent)' : 'var(--color-surface-2)', color: adminTab === tab.id ? 'white' : 'var(--color-label-secondary)', display: 'flex', flexDirection: 'column', padding: '8px 16px', gap: 1 }}>
            <span style={{ fontWeight: 500 }}>{lang === 'bn' ? tab.bn : tab.en}</span>
            <span style={{ fontSize: 9, opacity: adminTab === tab.id ? 0.7 : 0.4 }}>{lang === 'bn' ? tab.en : tab.bn}</span>
          </button>
        ))}
      </div>

      {/* REPORT TAB */}
      {adminTab === 'report' && (
        <div className="space-y-5">
          <div className="flex items-center gap-4 flex-wrap">
            <select value={selectedSchool} onChange={e => setSelectedSchool(e.target.value)} className="input-filled" style={{ width: 'auto', minWidth: 200 }}>
              <option value="">{lang === 'bn' ? 'সকল বিদ্যালয়' : 'All Schools'}</option>
              {schools.map(s => <option key={s.school_id} value={s.school_id}>{lang === 'bn' ? s.name_bangla : (s.name_english || s.name_bangla)}</option>)}
            </select>
            <ExportBar title="Attendance Report" data={report?.daily || []} columns={[
              {label:'Date',accessor:'date'},{label:'Present',accessor:'present'},
              {label:'Absent',accessor:'absent'},{label:'Late',accessor:'late'},{label:'Total',accessor:'total'}
            ]} />
          </div>
          {report && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="stat-card"><p className="stat-value" style={{ color: 'var(--color-accent)' }}>{report.attendance_rate}%</p><div className="bi-label"><span className="stat-label">{lang === 'bn' ? 'উপস্থিতির হার' : 'Attendance Rate'}</span><span style={{ fontSize: 11, opacity: 0.4, marginTop: 2 }}>{lang === 'bn' ? 'Attendance Rate' : 'উপস্থিতির হার'}</span></div></div>
                <div className="stat-card"><p className="stat-value">{report.total_records.toLocaleString()}</p><div className="bi-label"><span className="stat-label">{lang === 'bn' ? 'মোট রেকর্ড' : 'Total Records'}</span><span style={{ fontSize: 11, opacity: 0.4, marginTop: 2 }}>{lang === 'bn' ? 'Total Records' : 'মোট রেকর্ড'}</span></div></div>
                <div className="stat-card"><p className="stat-value" style={{ color: 'var(--color-warning)' }}>{report.daily?.length || 0}</p><div className="bi-label"><span className="stat-label">{lang === 'bn' ? 'কর্মদিবস' : 'School Days'}</span><span style={{ fontSize: 11, opacity: 0.4, marginTop: 2 }}>{lang === 'bn' ? 'School Days' : 'কর্মদিবস'}</span></div></div>
              </div>
              <div className="card-surface"><h3 style={{ fontSize: 'var(--text-subhead)', fontWeight: 600, color: 'var(--color-label-secondary)', marginBottom: 'var(--space-4)' }}>{lang === 'bn' ? 'দৈনিক উপস্থিতি বিস্তারিত' : 'Daily Attendance Breakdown'}</h3>
                <ResponsiveContainer minWidth={1} minHeight={1} width="100%" height={250}><BarChart data={report.daily}><CartesianGrid strokeDasharray="3 3" stroke="var(--color-separator)" /><XAxis dataKey="date" tickFormatter={d => d.slice(5)} tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} /><Tooltip /><Bar dataKey="present" fill="var(--color-accent)" name="Present" stackId="a" /><Bar dataKey="late" fill="var(--color-warning)" name="Late" stackId="a" /><Bar dataKey="absent" fill="var(--color-error)" name="Absent" stackId="a" /></BarChart></ResponsiveContainer>
              </div>
            </>
          )}
        </div>
      )}

      {/* ABSENCE APPROVAL TAB */}
      {adminTab === 'absence' && (
        <div className="space-y-4">
          <div className="card-surface" style={{ borderLeft: '4px solid var(--color-error)' }}>
            <h3 style={{ fontSize: 'var(--text-body)', fontWeight: 600 }}>{lang === 'bn' ? 'অনুপস্থিতি অনুমোদন কর্মপ্রবাহ' : 'Absence Approval Workflow'}</h3>
            <p style={{ fontSize: 11, color: 'var(--color-label-tertiary)', marginTop: 2 }}>{lang === 'bn' ? 'Absence Approval Workflow' : 'অনুপস্থিতি অনুমোদন কর্মপ্রবাহ'}</p>
            <p style={{ fontSize: 'var(--text-subhead)', color: 'var(--color-label-secondary)', marginTop: 'var(--space-2)', lineHeight: 1.6 }}>
              {lang === 'bn' ? '৩+ দিন অনুপস্থিত → শিক্ষককে সতর্ক → অভিভাবকদের সাথে যোগাযোগ → ৩ বার চেষ্টা → দীর্ঘ অনুপস্থিতি/নাম কাটা → প্রধান শিক্ষক পুনরায় ভর্তি করতে পারেন'
              : '3+ days absent → Alert teacher → Contact parents → 3 attempts → Long absence / Struck off → Principal can re-admit'}
            </p>
          </div>
          {ABSENCE_DATA.map((s, i) => (
            <div key={i} className="card-surface" style={{ padding: 'var(--space-4) var(--space-5)', borderLeft: `3px solid ${s.status === 'struck_off' ? 'var(--color-error)' : s.status === 'approved' ? 'var(--color-accent)' : 'var(--color-warning)'}` }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: 'var(--text-body)', fontWeight: 600 }}>{lang === 'bn' ? s.nameBn : s.name}</div>
                  <div style={{ fontSize: 10, color: 'var(--color-label-tertiary)', marginTop: 1 }}>{lang === 'bn' ? s.name : s.nameBn}</div>
                  <div style={{ fontSize: 'var(--text-footnote)', color: 'var(--color-label-secondary)', marginTop: 'var(--space-2)' }}>
                    {lang === 'bn' ? 'শ্রেণি' : 'Grade'} {s.grade} | {s.days} {lang === 'bn' ? 'দিন অনুপস্থিত' : 'days absent'} | {lang === 'bn' ? 'অভিভাবক চেষ্টা' : 'Parent attempts'}: {s.parentAttempts}/3
                  </div>
                  <div style={{ fontSize: 'var(--text-footnote)', color: 'var(--color-label-secondary)', marginTop: 2 }}>{lang === 'bn' ? 'কারণ' : 'Reason'}: {s.reason}</div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 'var(--space-2)' }}>
                  <span className={"badge " + (s.status === 'approved' ? 'badge-success' : s.status === 'struck_off' ? 'badge-error' : s.status === 'long_absence' ? 'badge-warning' : 'badge-neutral')}>
                    {s.status === 'approved' ? (lang === 'bn' ? 'অনুমোদিত' : 'APPROVED') : s.status === 'struck_off' ? (lang === 'bn' ? 'নাম কাটা' : 'STRUCK OFF') : s.status === 'long_absence' ? (lang === 'bn' ? 'দীর্ঘ অনুপস্থিতি' : 'LONG ABSENCE') : (lang === 'bn' ? 'অপেক্ষমাণ' : 'PENDING')}
                  </span>
                  {s.status === 'pending' && <button className="btn btn-sm" style={{ background: 'var(--color-accent)', color: 'white', padding: '4px 12px', fontSize: 11 }}>{lang === 'bn' ? 'অভিভাবককে সতর্ক করুন' : 'Alert Parent'}</button>}
                  {s.status === 'struck_off' && <button className="btn btn-sm" style={{ background: 'var(--color-accent)', color: 'white', padding: '4px 12px', fontSize: 11 }}>{lang === 'bn' ? 'পুনরায় ভর্তি' : 'Re-admit'}</button>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* LEADERBOARD TAB */}
      {adminTab === 'leaderboard' && (
        <div className="space-y-5">
          <div className="card-surface">
            <h3 style={{ fontSize: 'var(--text-body)', fontWeight: 600, marginBottom: 'var(--space-4)' }}>{lang === 'bn' ? 'উপস্থিতি র‍্যাংকিং — সেরা শিক্ষার্থী' : 'Attendance Rankings — Top Students'}</h3>
            <div style={{ fontSize: 11, color: 'var(--color-label-tertiary)', marginTop: -10, marginBottom: 'var(--space-3)' }}>{lang === 'bn' ? 'Top Students' : 'সেরা শিক্ষার্থী'}</div>
            {LEADERBOARD.map((s, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', padding: 'var(--space-3) 0', borderBottom: i < LEADERBOARD.length - 1 ? '1px solid var(--color-separator)' : 'none' }}>
                <span style={{ width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, background: i === 0 ? '#C9A84C' : i === 1 ? '#aaa' : i === 2 ? '#cd7f32' : 'var(--color-surface-2)', color: i < 3 ? 'white' : 'var(--color-label-secondary)' }}>{i + 1}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 'var(--text-subhead)', fontWeight: 500 }}>{lang === 'bn' ? s.nameBn : s.name}</div>
                  <div style={{ fontSize: 10, color: 'var(--color-label-tertiary)' }}>Grade {s.grade}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 'var(--text-body)', fontWeight: 700, color: 'var(--color-accent)' }}>{s.present}%</div>
                  <div style={{ fontSize: 10, color: 'var(--color-label-tertiary)' }}>{s.late} late | {s.absent} absent</div>
                </div>
              </div>
            ))}
          </div>
          <div className="card-surface" style={{ borderLeft: '3px solid var(--color-warning)' }}>
            <h3 style={{ fontSize: 'var(--text-body)', fontWeight: 600, marginBottom: 'var(--space-3)' }}>{lang === 'bn' ? 'নিয়মিত দেরিতে আসা শিক্ষার্থী' : 'Regular Latecomers'}</h3>
            <div style={{ fontSize: 11, color: 'var(--color-label-tertiary)', marginTop: -8, marginBottom: 'var(--space-3)' }}>{lang === 'bn' ? 'Regular Latecomers' : 'নিয়মিত দেরিতে আসা'}</div>
            {LATECOMER_DATA.map((s, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--space-3) 0', borderBottom: i < 2 ? '1px solid var(--color-separator)' : 'none' }}>
                <div><div style={{ fontSize: 'var(--text-subhead)', fontWeight: 500 }}>{lang === 'bn' ? s.nameBn : s.name}</div><div style={{ fontSize: 10, color: 'var(--color-label-tertiary)' }}>Grade {s.grade}</div></div>
                <div style={{ textAlign: 'right' }}><span className="badge badge-warning">{s.lateDays} {lang === 'bn' ? 'দিন' : 'days'}</span><div style={{ fontSize: 10, color: 'var(--color-label-tertiary)', marginTop: 4 }}>Avg {s.avgMinutesLate} min late</div></div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Attendance() {
  const { isTeacher } = useApp();
  return isTeacher ? <AttendanceMarking /> : <AttendanceReport />;
}
